// src/components/crm/EmployeeProfileModal.js

import React, { useState, useEffect } from 'react';
import { X, Trash2, UploadCloud, Calendar, AlertTriangle, FileText, ExternalLink, ShieldAlert, Loader2, Check, UserMinus } from 'lucide-react';
import { formatPhoneNumber } from '../../utils/formatters';
import { httpsCallable } from 'firebase/functions'; 
import { db, functions } from '../../firebase/config'; 
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { countries, states } from '../../utils/data'; 
import { parseISO } from 'date-fns';
import useStorage from '../../hooks/useStorage'; 
import { can, PERMISSIONS } from '../../utils/permissions';
import { TerminationModal } from './TeamMemberModal';
import { PROFILE_PRIVATE_FIELDS, resolveProfileRead } from '../../utils/profilePrivacy'; // profcut-4b9d2e63

const EmployeeProfileModal = ({ closeModal, editingEmployee, currentUserProfile }) => {
    // tmdupe-4c7a1e93 — same bug as TeamMemberModal, with a worse consequence. This modal stays
    // open after a create ("...continue editing") while a prop-derived flag kept it in "create"
    // mode, so a second Save called addUser again. A same-email repeat failed safely (Auth refuses
    // a duplicate address), but the email box stayed editable — so CORRECTING A TYPO and saving
    // created a second account, consumed a second plan seat and emailed an invite to a stranger.
    // Hold the uid of whatever user this modal owns; `isNew` now means "no user yet".
    const [createdUid, setCreatedUid] = useState(null);
    const targetUid = editingEmployee?.id || createdUid;
    const isNew = !targetUid;
    // True only for a user created in THIS session and not yet reopened. Used to protect the
    // 'invited' status below — see handleSave.
    const justCreated = !editingEmployee && !!createdUid;
    const [activeTab, setActiveTab] = useState('main');
    const [managers, setManagers] = useState([]); 
    
    // Use the custom storage hook
    const { uploadFile, deleteFile, progress, isUploading } = useStorage();
    
    // Track which specific field is currently uploading
    const [activeUploadField, setActiveUploadField] = useState(null);

    const emp = editingEmployee || {};

    const initialFormData = {
        // I. CORE IDENTITY
        name: emp.name || '',
        email: emp.email || '',
        password: '', 
        passwordVerification: '', 
        role: emp.role || 'Field Technician', 
        phone: emp.phone || '',
        status: emp.status || 'active',
        managerId: emp.managerId || '',

        // II. NEW IDENTITY FIELDS
        homeAddress1: emp.homeAddress1 || '', 
        homeAddress2: emp.homeAddress2 || '',
        homeCity: emp.homeCity || '',
        homeState: emp.homeState || '',
        homeZip: emp.homeZip || '',
        homeCountry: emp.homeCountry || 'US', 

        birthDate: emp.birthDate?.toDate?.()?.toISOString().split('T')[0] || '', 
        startDate: emp.startDate?.toDate?.()?.toISOString().split('T')[0] || '',
        legalName: emp.legalName || '', 
        
        // III. COMPENSATION & PAYROLL
        // r8-comp-private-5d2c9f41 — hourlyRate/salaryWage/laborCostRate are NOT on the user
        // doc the `emp` prop comes from (they live in users/{id}/private/comp, which only
        // high-level roles can read). Seed them to 0 and load them in the effect below.
        // billableRate stays on the parent doc — all staff may see it — so it still seeds here.
        compensationType: emp.compensationType || 'Hourly',
        hourlyRate: 0,
        salaryWage: 0,
        billableRate: emp.billableRate || 0, // r5-b2b-seed-2f9a1d4e — B2b bill-out rate (Increment 1, inert)
        laborCostRate: 0,
        payFrequency: emp.payFrequency || 'Bi-Weekly',
        bonusPlan: emp.bonusPlan || '', 
        isExempt: emp.isExempt || false,

        // III-B. WORK SCHEDULE (overrides org default when set; '' = use org default)
        schedulePreset: emp.schedulePreset || '',       // '' | '5x8' | '4x10' | '3x12' | 'custom'
        workDays: emp.workDays || null,                 // null = inherit org default; array of day keys when set
        workDaysPerWeek: emp.workDaysPerWeek || '',     // only used when schedulePreset = 'custom'
        workHoursPerDay: emp.workHoursPerDay || '',     // only used when schedulePreset = 'custom'
        shiftTrackingMode: emp.shiftTrackingMode || '', // '' | 'strict' | 'estimate' | 'off'

        // IV. TIME OFF
        vacationWeeksAllotted: emp.vacationWeeksAllotted || 2, 
        timeOffHistory: emp.timeOffHistory || [], 
        
        // V. EDUCATION
        undergradDegree: emp.undergradDegree || '',
        mastersDegree: emp.mastersDegree || '',
        doctorateDegree: emp.doctorateDegree || '',
        
        // VI. DOCUMENTS
        directDepositFormUrl: emp.directDepositFormUrl || '',
        certifications: emp.certifications || [], 
        disciplinaryForms: emp.disciplinaryForms || [], 

        contactDetails: emp.contactDetails || '', 

        // VII. EMERGENCY CONTACT
        emergencyName: emp.emergencyName || '',
        emergencyRelationship: emp.emergencyRelationship || '',
        emergencyPhone1: emp.emergencyPhone1 || '',
        emergencyPhone2: emp.emergencyPhone2 || '',
        emergencyEmail: emp.emergencyEmail || '',
    };

    const [formData, setFormData] = useState(initialFormData);
    const [showTerminate, setShowTerminate] = useState(false);
    const [newTimeOff, setNewTimeOff] = useState({ start: '', end: '', type: 'Vacation', notes: '', file: null }); 
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Formatted display value for salary input — shows commas as user types
    const [salaryDisplayValue, setSalaryDisplayValue] = useState(() => {
        const val = parseFloat(initialFormData.salaryWage) || 0;
        return val > 0 ? val.toLocaleString('en-CA') : '';
    });

    // r8-comp-private-5d2c9f41 — private comp lives in users/{id}/private/comp, readable only by
    // Owner/Admin/Manager. Load it on open for an existing employee. Until it resolves (or if the
    // read is denied) the comp inputs are disabled and comp is OMITTED from the save payload — a
    // form that never saw the real rates must never write its placeholder zeros over them.
    // tmdupe-4c7a1e93 — keyed on the PROP, not `isNew`: this loader is for a user opened for
    // editing. After an in-session create the form already holds the truth, and re-running it
    // would be a wasted read that could clobber what is being typed.
    const [compState, setCompState] = useState(editingEmployee?.id ? 'loading' : 'ready'); // 'loading' | 'ready' | 'denied'
    const compEditable = compState === 'ready';

    useEffect(() => {
        if (!editingEmployee?.id) return;
        let cancelled = false;

        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', editingEmployee.id, 'private', 'comp'));
                if (cancelled) return;
                const c = snap.exists() ? snap.data() : {};
                setFormData(prev => ({
                    ...prev,
                    hourlyRate: c.hourlyRate ?? 0,
                    salaryWage: c.salaryWage ?? 0,
                    laborCostRate: c.laborCostRate ?? 0,
                }));
                const sal = parseFloat(c.salaryWage) || 0;
                setSalaryDisplayValue(sal > 0 ? sal.toLocaleString('en-CA') : '');
                setCompState('ready');
            } catch (err) {
                if (cancelled) return;
                console.warn('Compensation is not visible to your role:', err?.message);
                setCompState('denied');
            }
        })();

        return () => { cancelled = true; };
    }, [editingEmployee?.id]);

    // profcut-4b9d2e63 — personal details (legal name, birth/start date, home address, emergency
    // contact, internal notes, time off, degrees, HR documents) live in users/{id}/private/profile,
    // readable only by Owner/Admin/Manager — the main user record every org member can list no longer
    // holds them. Load on open; a key on the private record wins over a not-yet-moved main-record
    // copy. updateUser DELETES any personal field a save leaves blank, so until this read resolves the
    // save is blocked, and if it is denied the personal fields are stripped from the payload.
    // tmdupe-4c7a1e93 — keyed on the PROP, not `isNew` (see the comp loader above).
    const [profileState, setProfileState] = useState(editingEmployee?.id ? 'loading' : 'ready'); // 'loading' | 'ready' | 'denied'

    useEffect(() => {
        if (!editingEmployee?.id) return;
        let cancelled = false;

        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', editingEmployee.id, 'private', 'profile'));
                if (cancelled) return;
                const p = resolveProfileRead(editingEmployee, snap.exists() ? snap.data() : {});
                setFormData(prev => ({
                    ...prev,
                    homeAddress1: p.homeAddress1 || '',
                    homeAddress2: p.homeAddress2 || '',
                    homeCity: p.homeCity || '',
                    homeState: p.homeState || '',
                    homeZip: p.homeZip || '',
                    homeCountry: p.homeCountry || 'US',
                    birthDate: p.birthDate?.toDate?.()?.toISOString().split('T')[0] || '',
                    startDate: p.startDate?.toDate?.()?.toISOString().split('T')[0] || '',
                    legalName: p.legalName || '',
                    vacationWeeksAllotted: p.vacationWeeksAllotted || 2,
                    timeOffHistory: p.timeOffHistory || [],
                    undergradDegree: p.undergradDegree || '',
                    mastersDegree: p.mastersDegree || '',
                    doctorateDegree: p.doctorateDegree || '',
                    directDepositFormUrl: p.directDepositFormUrl || '',
                    certifications: p.certifications || [],
                    disciplinaryForms: p.disciplinaryForms || [],
                    contactDetails: p.contactDetails || '',
                    emergencyName: p.emergencyName || '',
                    emergencyRelationship: p.emergencyRelationship || '',
                    emergencyPhone1: p.emergencyPhone1 || '',
                    emergencyPhone2: p.emergencyPhone2 || '',
                    emergencyEmail: p.emergencyEmail || '',
                }));
                setProfileState('ready');
            } catch (err) {
                if (cancelled) return;
                console.warn('Personal details are not visible to your role:', err?.message);
                setProfileState('denied');
            }
        })();

        return () => { cancelled = true; };
    }, [editingEmployee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSalaryInput = (e) => {
        const raw = e.target.value.replace(/,/g, ''); // strip existing commas
        if (raw === '' || raw === '.') {
            setSalaryDisplayValue(raw);
            setFormData(prev => ({ ...prev, salaryWage: 0 }));
            return;
        }
        if (isNaN(parseFloat(raw))) return; // ignore non-numeric input
        // Format integer part with commas, preserve decimal as typed
        const parts = raw.split('.');
        const formatted = parseInt(parts[0], 10).toLocaleString('en-CA') + (parts.length > 1 ? '.' + parts[1] : '');
        setSalaryDisplayValue(formatted);
        setFormData(prev => ({ ...prev, salaryWage: parseFloat(raw) }));
    };

    const SCHEDULE_PRESETS = { '5x8': [5, 8], '4x10': [4, 10], '3x12': [3, 12] };

    const PRESET_WORK_DAYS = {
        '5x8':  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        '4x10': ['monday', 'tuesday', 'wednesday', 'thursday'],
        '3x12': ['monday', 'wednesday', 'friday'],
    };

    const ALL_DAYS = [
        { key: 'sunday',    label: 'Sun' },
        { key: 'monday',    label: 'Mon' },
        { key: 'tuesday',   label: 'Tue' },
        { key: 'wednesday', label: 'Wed' },
        { key: 'thursday',  label: 'Thu' },
        { key: 'friday',    label: 'Fri' },
        { key: 'saturday',  label: 'Sat' },
    ];

    // Only Owner/Admin roles can edit payroll schedule settings
    const canEditSchedule = ['Admin', 'Owner', 'Owner/System Admin', 'System Admin'].includes(currentUserProfile?.role);

    const handleScheduleChange = (field, value) => {
        if (!canEditSchedule) return;
        setFormData(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'schedulePreset' && SCHEDULE_PRESETS[value]) {
                updated.workDaysPerWeek = SCHEDULE_PRESETS[value][0];
                updated.workHoursPerDay = SCHEDULE_PRESETS[value][1];
                // Pre-populate work days from preset — user can adjust after
                if (PRESET_WORK_DAYS[value]) updated.workDays = [...PRESET_WORK_DAYS[value]];
            }
            if (field === 'schedulePreset' && value === '') {
                // Reverting to org default — clear custom days too
                updated.workDays = null;
            }
            return updated;
        });
    };

    const handleDayToggle = (dayKey) => {
        if (!canEditSchedule) return;
        setFormData(prev => {
            const current = prev.workDays || PRESET_WORK_DAYS[prev.schedulePreset] || [];
            const isSelected = current.includes(dayKey);
            // Custom preset = uncapped (grid defines the schedule freely)
            // Named presets = hard cap at the preset's day count
            const cap = prev.schedulePreset === 'custom'
                ? 7
                : SCHEDULE_PRESETS[prev.schedulePreset]?.[0] ?? 7;
            if (!isSelected && current.length >= cap) return prev;
            const next = isSelected
                ? current.filter(d => d !== dayKey)
                : [...current, dayKey];
            return { ...prev, workDays: next, workDaysPerWeek: next.length };
        });
    };
    
    // Calculate totals dynamically from the history array
    const calculateDaysTaken = (type) => {
        return (formData.timeOffHistory || [])
            .filter(entry => entry.type === type && entry.status === 'Approved')
            .reduce((sum, entry) => sum + (Number(entry.days) || 0), 0);
    };

    const daysAllotted = (formData.vacationWeeksAllotted || 0) * 5;
    const vacationDaysTaken = calculateDaysTaken('Vacation');
    const vacationDaysRemaining = daysAllotted - vacationDaysTaken;
    const sickDaysTaken = calculateDaysTaken('Sick');
    const personalDaysTaken = calculateDaysTaken('Personal');

    const availableRoles = ['Field Technician', 'Office Staff', 'Sales Consultant', 'Manager', 'Admin', 'Owner/System Admin'];
    const payFrequencies = ['Weekly', 'Bi-Weekly', 'Monthly', 'Semi-Monthly'];

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        
        // Allow Owners to see this too
        const allowedRoles = ['Manager', 'Admin', 'Owner/System Admin', 'Owner', 'System Admin'];
        const hasPermission = allowedRoles.some(role => currentUserProfile.role === role || currentUserProfile.role.includes('Owner'));
        
        if (!hasPermission) {
            return; 
        }

        const orgId = currentUserProfile.organizationId;
        const usersRef = collection(db, 'users');
        
        const q = query(
            usersRef, 
            where("organizationId", "==", orgId),
            where('role', 'in', ['Manager', 'Admin', 'Owner/System Admin', 'Owner', 'System Admin'])
        );

        const unsubscribe = onSnapshot(q, 
            (snapshot) => {
                setManagers(snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name, role: doc.data().role })));
            }, 
            (err) => {
                console.warn("Failed to load managers list:", err);
                setManagers([]); 
            }
        );
        return unsubscribe;
    }, [currentUserProfile?.organizationId, currentUserProfile?.role]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let val;

        if (type === 'checkbox') {
            val = checked;
        } else if (['hourlyRate', 'salaryWage', 'billableRate', 'laborCostRate', 'vacationWeeksAllotted'].includes(name)) { // r6-b2a-numlist-8c3f2d47 
            val = value === '' ? '' : parseFloat(value);
        } else if (type === 'tel') {
            val = formatPhoneNumber(value);
        } else {
            val = value;
        }

        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const validateForm = () => {
        if (!formData.name || !formData.email || !formData.role) {
            setError("Name, Email, and Role are required.");
            return false;
        }
        setError('');
        return true;
    };
    
    // --- UPDATED FILE UPLOAD LOGIC ---
    const handleFileUpload = async (e, fieldName) => {
        const file = e.target.files[0];
        if (!file || !currentUserProfile?.organizationId) return;

        if (isNew) { alert("Please create the user first before uploading documents."); return; }

        setActiveUploadField(fieldName);
        setError('');

        // tmdupe-4c7a1e93 — targetUid, not the prop: after an in-session create this path would
        // have contained the literal "undefined".
        const storagePath = `organizations/${currentUserProfile.organizationId}/employees/${targetUid}/hr_docs/${fieldName}_${Date.now()}_${file.name}`;
        
        try {
            const downloadURL = await uploadFile(file, storagePath);

            if (fieldName === 'directDepositFormUrl') {
                setFormData(prev => ({ ...prev, directDepositFormUrl: downloadURL }));
            } else {
                const newDoc = { 
                    name: file.name, 
                    url: downloadURL, 
                    uploadedAt: new Date().toISOString(), 
                    type: fieldName,
                    storagePath: storagePath 
                };
                setFormData(prev => ({ ...prev, [fieldName]: [...(prev[fieldName] || []), newDoc] }));
            }
            setSuccessMessage(`${fieldName === 'directDepositFormUrl' ? 'Form' : 'Document'} uploaded successfully! Click Save to confirm.`);

        } catch (uploadError) {
            console.error("Error uploading file:", uploadError);
            setError('File upload failed.');
        } finally {
            setActiveUploadField(null);
        }
    };
    
    const handleDeleteDocument = async (fileName, fieldName, docPath = null) => {
        if(!window.confirm("Are you sure you want to remove this document?")) return;
        
        if (docPath) {
            try {
                await deleteFile(docPath);
            } catch (err) {
                console.error("Warning: Could not delete file from storage bucket", err);
            }
        }

        if (fieldName === 'directDepositFormUrl') {
             setFormData(prev => ({ ...prev, directDepositFormUrl: '' }));
        } else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: prev[fieldName].filter(doc => doc.name !== fileName)
            }));
        }
        setSuccessMessage('Document removed. Click Save to confirm change.');
    };

    // --- TIME OFF LOGIC ---
    const handleAddTimeOff = async () => {
        if (!newTimeOff.start || !newTimeOff.end) {
            alert("Please select both start and end dates.");
            return;
        }
        if (isNew) {
            alert("Please save the new employee before logging time off.");
            return;
        }
        
        let docUrl = '';
        
        if (newTimeOff.file) {
             try {
                const path = `organizations/${currentUserProfile.organizationId}/employees/${targetUid}/timeoff/${newTimeOff.file.name}_${Date.now()}`;
                docUrl = await uploadFile(newTimeOff.file, path);
             } catch (e) {
                 console.error("Upload failed", e);
                 alert("Failed to upload document.");
                 return;
             }
        }

        const start = parseISO(newTimeOff.start);
        const end = parseISO(newTimeOff.end);
        let daysDiff = 0;
        
        if (start <= end) {
             const diffTime = Math.abs(end - start);
             daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 
        }

        // Write a calendar event to Firestore immediately so it shows on the shared calendar
        let calendarEventId = null;
        try {
            const orgId = currentUserProfile.organizationId;
            const calendarEnd = new Date(end);
            calendarEnd.setHours(23, 59, 59, 0);
            const calendarRef = await addDoc(collection(db, `organizations/${orgId}/jobs`), {
                eventType: 'Time Off',
                timeOffType: newTimeOff.type,
                // tmdupe-4c7a1e93 — name off the form, id off targetUid. `editingEmployee` is null
                // for a user created in this session, which would have put "undefined — Vacation"
                // on the shared calendar.
                title: `${formData.name} — ${newTimeOff.type}`,
                clientName: formData.name,
                assignedTo: [targetUid],
                assignedToNames: [formData.name],
                startAt: start,
                endAt: calendarEnd,
                status: 'Scheduled',
                createdAt: serverTimestamp(),
            });
            calendarEventId = calendarRef.id;
        } catch (e) {
            console.error("Failed to create calendar event for time off:", e);
        }

        const newEntry = {
            id: Date.now().toString(),
            startDate: newTimeOff.start,
            endDate: newTimeOff.end,
            type: newTimeOff.type,
            notes: newTimeOff.notes,
            status: 'Approved', 
            days: daysDiff,
            docUrl: docUrl,
            docName: newTimeOff.file ? newTimeOff.file.name : '',
            calendarEventId,
        };

        setFormData(prev => ({
            ...prev,
            timeOffHistory: [newEntry, ...(formData.timeOffHistory || [])]
        }));
        
        setNewTimeOff({ start: '', end: '', type: 'Vacation', notes: '', file: null });
    };

    const handleDeleteTimeOff = async (id) => {
        if (!window.confirm("Are you sure you want to delete this time off entry?")) return;
        
        // Delete the corresponding calendar event if one was created
        const entry = formData.timeOffHistory.find(item => item.id === id);
        if (entry?.calendarEventId && currentUserProfile?.organizationId) {
            try {
                await deleteDoc(doc(db, `organizations/${currentUserProfile.organizationId}/jobs`, entry.calendarEventId));
            } catch (e) {
                console.error("Failed to delete calendar event for time off:", e);
            }
        }

        setFormData(prev => ({
            ...prev,
            timeOffHistory: prev.timeOffHistory.filter(item => item.id !== id)
        }));
        setSuccessMessage('Time off entry removed. Click Save to confirm change.');
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        // profcut-4b9d2e63 — never save before the personal details have loaded: the form still
        // holds main-record values (blank once they have moved), and updateUser deletes blanks.
        if (profileState === 'loading') {
            setError('Still loading personal details — please try again in a moment.');
            return;
        }

        // --- 1. CRITICAL SAFETY CHECK ---
        // If the org ID is missing, the cloud function WILL fail. Catch it here.
        if (isNew && !currentUserProfile?.organizationId) {
            console.error("CRITICAL: organizationId is missing from currentUserProfile", currentUserProfile);
            setError("System Error: Your organization ID is missing. Please refresh the page and try again.");
            return;
        }

        let orphanClients = false;
        if (!isNew && formData.status === 'inactive' && editingEmployee?.status === 'active') {
            const confirmOrphan = window.confirm(
                `You are marking ${formData.name} as Inactive.\n\nDo you want to move their assigned Customers to the "Orphaned" list so they can be reassigned?`
            );
            orphanClients = confirmOrphan;
        }
        
        setIsSaving(true);
        setSuccessMessage('');
        
        // tmdupe-4c7a1e93 — routed by the uid this modal owns, not by the prop it opened with.
        const callableFunction = httpsCallable(functions, targetUid ? 'updateUser' : 'addUser');
        
        try {
            const dataToSave = {
                ...formData,
                orphanClients: orphanClients,
                // r8-comp-private-5d2c9f41 — the three private comp fields ride along ONLY when the
                // form actually loaded them (compEditable). If the read was denied or still pending,
                // they're stripped below so the server leaves the stored rates untouched.
                // `compSource: 'private'` tells the server this bundle knows about the private doc —
                // at cutover the callables ignore comp from any payload lacking this marker, which
                // is what stops a stale cached browser tab from saving zeros over real wages.
                compSource: 'private',
                hourlyRate: parseFloat(formData.hourlyRate) || 0,
                salaryWage: parseFloat(formData.salaryWage) || 0,
                billableRate: parseFloat(formData.billableRate) || 0, // r5-b2b-save-8c1f3a97
                laborCostRate: parseFloat(formData.laborCostRate) || 0, // r6-b2a-save-2d9e7f18
                vacationWeeksAllotted: parseInt(formData.vacationWeeksAllotted) || 0,
                birthDate: formData.birthDate || null, 
                startDate: formData.startDate || null,
                // Work schedule — store null (not '') when using org default so Firestore doesn't hold stale empty strings
                schedulePreset: formData.schedulePreset || null,
                workDays: formData.workDays || null,
                workDaysPerWeek: formData.workDaysPerWeek !== '' ? Number(formData.workDaysPerWeek) : null,
                workHoursPerDay: formData.workHoursPerDay !== '' ? Number(formData.workHoursPerDay) : null,
                shiftTrackingMode: formData.shiftTrackingMode || null,
                // Ensure we don't send undefined for things we don't track here
                vacationDaysTaken: undefined, 
                sickDaysTaken: undefined,
                personalDaysTaken: undefined,
            };
            
            delete dataToSave.passwordVerification;

            // r8-comp-private-5d2c9f41 — never write comp we didn't load. `undefined` is what the
            // callables already treat as "leave this field alone" (see updateUser's !== undefined
            // guards), so stripping the keys is the correct no-op.
            if (!compEditable) {
                delete dataToSave.hourlyRate;
                delete dataToSave.salaryWage;
                delete dataToSave.laborCostRate;
            }

            // profcut-4b9d2e63 — `profileSource: 'private'` tells updateUser this form loaded the
            // private profile; without it the server discards personal fields (a stale tab showing
            // blanks must not erase the only copy). If the read was denied, send none at all.
            if (profileState === 'ready') {
                dataToSave.profileSource = 'private';
            } else {
                for (const f of PROFILE_PRIVATE_FIELDS) delete dataToSave[f];
            }

            // 🛑 tmdupe-4c7a1e93 — THE INVITED-STATUS TRAP. The form seeds `status: emp.status ||
            // 'active'`, so for a brand-new employee `status` is 'active'. updateUser does
            // `if (status) updateData.status = status;`, so a second save would silently flip a
            // user from 'invited' to 'active' BEFORE THEY HAVE EVER LOGGED IN. That looks fine on
            // screen and then breaks the invite: resendInvite refuses anyone whose status is not
            // 'invited', leaving the person unable to sign in. So while this modal is still
            // showing a user it created in this session, `status` is not sent at all. Once the
            // window is closed and reopened, editingEmployee.status is real and it is sent
            // normally. The Status field is shown read-only as "Invited" in the same state.
            if (justCreated) delete dataToSave.status;

            let response;
            if (!targetUid) {
                // --- 2. DEBUG LOG & DOUBLE-PAYLOAD ---
                console.log("Creating user for Org:", currentUserProfile.organizationId);

                response = await callableFunction({
                    ...dataToSave,
                    organizationId: currentUserProfile.organizationId,
                    orgId: currentUserProfile.organizationId // Send both just in case backend uses short name
                });
                // tmdupe-4c7a1e93 — remember who we just made, so the next Save updates them
                // instead of creating a second account. addUser returns the uid for this.
                if (response?.data?.uid) setCreatedUid(response.data.uid);
            } else {
                response = await callableFunction({
                    ...dataToSave,
                    uid: targetUid,
                });
            }

            setSuccessMessage(response.data.result || 'Saved successfully! You can close this window or continue editing.');

        } catch (error) {
            console.error("Error saving employee profile:", error);
            setError(error.details?.message || error.message || 'An unexpected error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    // tmdupe-4c7a1e93 — targetUid + formData.name throughout: `editingEmployee` is null for a user
    // created in this session. (No button currently calls this handler, but leaving the null
    // dereference in place would be a landmine for whoever wires one up.)
    const handleDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete ${formData.name}? This action cannot be undone.`)) return;

        try {
            const deleteUser = httpsCallable(functions, 'deleteUser');
            await deleteUser({ uid: targetUid });
            alert(`${formData.name} has been deleted.`);
            closeModal();
        } catch (error) {
            console.error("Error deleting user:", error);
            setError(error.details?.message || error.message || 'Failed to delete employee.');
        }
    };

    // Downgrade a User to a Team Member: disables login + frees the seat, keeps
    // them schedulable with their full profile. Same account is reused on re-upgrade.
    const handleDowngrade = async () => {
        if (!window.confirm(
            `Downgrade ${formData.name} to a Team Member?\n\n` + // tmdupe-4c7a1e93
            `They'll lose app access and their seat frees up immediately — but they stay ` +
            `schedulable on routes and keep their full profile and history. You can upgrade ` +
            `them again anytime, and it'll be the same account.`
        )) return;

        setIsSaving(true);
        setError('');
        try {
            const downgradeUserToTeamMember = httpsCallable(functions, 'downgradeUserToTeamMember');
            const res = await downgradeUserToTeamMember({ uid: targetUid });
            alert(res?.data?.result || `${formData.name} is now a Team Member.`);
            closeModal();
        } catch (error) {
            console.error("Error downgrading user:", error);
            setError(error.details?.message || error.message || 'Failed to downgrade user.');
            setIsSaving(false);
        }
    };

    const DocumentUploadSection = ({ title, fieldName, docs, icon: Icon }) => (
        <div className="mb-6">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Icon size={18} className="text-gray-500" />
                {title}
            </h3>

            {isNew ? (
                <div className="bg-yellow-50 p-3 rounded-xl border border-yellow-200 text-sm text-yellow-700 flex items-center mb-3">
                    <AlertTriangle size={16} className="mr-2" />
                    Please save employee before uploading documents.
                </div>
            ) : (
                <div className="mb-4">
                    {isUploading && activeUploadField === fieldName ? (
                        <div className="w-full bg-gray-100 rounded-xl p-4 border border-gray-200">
                             <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-xs text-center text-gray-500">Uploading... {Math.round(progress)}%</p>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:bg-gray-50 transition-colors relative group cursor-pointer">
                            <input 
                                type="file" 
                                onChange={(e) => handleFileUpload(e, fieldName)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            />
                            <UploadCloud className="mx-auto h-8 w-8 text-gray-400 group-hover:text-blue-500 transition-colors mb-2" />
                            <p className="text-sm text-gray-600 font-medium">Click to upload document</p>
                            <p className="text-xs text-gray-400 mt-1">PDF, Word, Images (Max 10MB)</p>
                        </div>
                    )}
                </div>
            )}

            <div className="space-y-2">
                {docs.map((doc, index) => (
                    <div key={index} className="flex justify-between items-center bg-blue-50 p-3 rounded-xl border border-blue-100">
                        <div className="flex items-center gap-3 overflow-hidden">
                            <FileText size={20} className="text-blue-500 flex-shrink-0" />
                            <div className="flex flex-col min-w-0">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-700 hover:underline truncate">
                                    {doc.name}
                                </a>
                                <span className="text-xs text-gray-400">
                                    {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'Unknown date'}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-2 text-blue-600 hover:bg-blue-100 rounded" title="View">
                                <ExternalLink size={16} />
                            </a>
                            <button 
                                onClick={() => handleDeleteDocument(doc.name, fieldName, doc.storagePath)} 
                                className="p-2 text-red-500 hover:bg-red-100 rounded" 
                                title="Remove"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))}
                {docs.length === 0 && !isNew && (
                    <p className="text-xs text-gray-400 italic">No documents uploaded yet.</p>
                )}
            </div>
        </div>
    );

    const TabButton = ({ name, label }) => (
        <button
            onClick={() => setActiveTab(name)}
            className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors -mb-px ${
                activeTab === name
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
            {label}
        </button>
    );

    // --- RENDER MAIN ---
    const renderMainInfo = () => (
        <div className="space-y-4">
            {/* profcut-4b9d2e63 — personal details load from the private profile doc. */}
            {profileState === 'denied' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                    <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Personal details (address, birth date, emergency contact, documents, time off) are visible to Owners, Admins and Managers only. Changes to them won't be saved.</span>
                </div>
            )}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="John Doe" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Legal Name (For Payroll)</label>
                    <input type="text" name="legalName" value={formData.legalName} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="John Michael Doe" />
                </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Email (Login)</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500" placeholder="user@company.com" disabled={!isNew} />
                    {/* tmdupe-4c7a1e93 — updateUser does not accept `email`, so an edit here after
                        the account exists would silently do nothing. The input locks itself (it is
                        already disabled={!isNew}); this says why, which is the part that was
                        missing when someone tried to correct a typo and got a second account. */}
                    {!isNew && (
                        <p className="mt-1 text-xs text-gray-500">
                            Email can't be changed here — remove the user and re-add them.
                        </p>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
            </div>

            {isNew && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    We'll email <span className="font-medium">{formData.email || 'this person'}</span> a secure link to set their own password. They won't be able to log in until they do — no need to create a password here.
                </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select name="role" value={formData.role} onChange={handleChange} className="mt-1 w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        {availableRoles.map(role => {
                            const isOwnerOption = ['Owner', 'Owner/System Admin', 'Owner / System Admin'].includes(role);
                            const isAdminOption = role === 'Admin';
                            const amIOwner = ['Owner', 'Owner/System Admin', 'Owner / System Admin', 'System Admin'].includes(currentUserProfile.role);
                            const amIAdmin = currentUserProfile.role === 'Admin';

                            let isDisabled = false;
                            if (isOwnerOption && !amIOwner) isDisabled = true;
                            if (isAdminOption && !amIOwner && !amIAdmin) isDisabled = true;
                            // s9harden-3c7e1a59 — a Manager can't make someone a Manager (server roleGate
                            // refuses it); someone who is ALREADY a Manager keeps the option selected.
                            if (role === 'Manager' && currentUserProfile.role === 'Manager' && editingEmployee?.role !== 'Manager') isDisabled = true;

                            return (
                                <option key={role} value={role} disabled={isDisabled}>
                                    {role}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Manager</label>
                    <select name="managerId" value={formData.managerId} onChange={handleChange} className="mt-1 w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        <option value="">-- No Manager --</option>
                        {managers.map(m => (
                            <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    {/* tmdupe-4c7a1e93 — a user created in this session is 'invited' until they log
                        in, and this dropdown has no such option, so leaving it showing "Active"
                        would be a lie. Matches handleSave, which does not send `status` in this
                        state. It becomes a normal dropdown once the window is reopened. */}
                    {justCreated ? (
                        <div className="mt-1 w-full border border-gray-200 bg-gray-100 rounded-xl px-3 py-2.5 text-sm text-gray-600">
                            Invited — status is set when they first log in.
                        </div>
                    ) : (
                    <select name="status" value={formData.status} onChange={handleChange} className="mt-1 w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="on_leave">On Leave</option>
                    </select>
                    )}
                </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <input type="date" name="birthDate" value={formData.birthDate} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
            </div>
            
            <div className="pt-2 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-2">Home Address</label>
                <div className="space-y-2">
                    <input type="text" name="homeAddress1" value={formData.homeAddress1} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Address Line 1" />
                    <input type="text" name="homeAddress2" value={formData.homeAddress2} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Address Line 2 (Optional)" />
                    <div className="grid grid-cols-2 gap-2">
                         <input type="text" name="homeCity" value={formData.homeCity} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="City" />
                         <input type="text" name="homeZip" value={formData.homeZip} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Zip / Postal Code" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                         <select name="homeCountry" value={formData.homeCountry} onChange={handleChange} className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                            {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                        <select name="homeState" value={formData.homeState} onChange={handleChange} className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                            <option value="">-- State/Prov --</option>
                            {(states[formData.homeCountry] || []).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Internal Contact Notes</label>
                <textarea name="contactDetails" value={formData.contactDetails} onChange={handleChange} rows="3" className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
            </div>
        </div>
    );

    const renderWages = () => (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold">Compensation Details</h3>
            <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Pay Frequency</label>
                    <select name="payFrequency" value={formData.payFrequency} onChange={handleChange} className="mt-1 w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        {payFrequencies.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Employee Type</label>
                    <select name="compensationType" value={formData.compensationType} onChange={handleChange} className="mt-1 w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        <option value="Hourly">Hourly</option>
                        <option value="Salary">Salary</option>
                    </select>
                </div>
            </div>
            
            {/* r8-comp-private-5d2c9f41 — wage fields load from the private comp doc; until they do
                (or if this role can't read it) they're disabled and excluded from the save. */}
            {compState === 'denied' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                    <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Wage details are visible to Owners, Admins and Managers only. Everything else on this profile can still be edited and saved.</span>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                {formData.compensationType === 'Hourly' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Hourly Rate</label>
                        <input type="number" name="hourlyRate" value={compEditable ? formData.hourlyRate : ''} onChange={handleChange} disabled={!compEditable} placeholder={compState === 'loading' ? 'Loading…' : compState === 'denied' ? 'Hidden' : 'e.g. 25.00'} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400" />
                    </div>
                )}
                {formData.compensationType === 'Salary' && (
                     <div>
                        <label className="block text-sm font-medium text-gray-700">Annual Salary/Wage</label>
                        <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input
                                type="text"
                                inputMode="numeric"
                                name="salaryWage"
                                value={compEditable ? salaryDisplayValue : ''}
                                onChange={handleSalaryInput}
                                disabled={!compEditable}
                                className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                placeholder={compState === 'loading' ? 'Loading…' : compState === 'denied' ? 'Hidden' : 'e.g. 60,000'}
                            />
                        </div>
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700">FLSA Exemption Status</label>
                    <label className="flex items-center mt-2">
                         <input type="checkbox" name="isExempt" checked={formData.isExempt} onChange={handleChange} className="h-4 w-4" />
                        <span className="ml-2 text-sm">Exempt from Overtime</span>
                    </label>
                </div>
                {/* r5-b2b-billrate-ui-3d7b2e08 — B2b bill-out rate (Increment 1, inert; customer charge-out, not pay) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Billable Rate <span className="font-normal text-gray-400">(to customers)</span></label>
                    <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input type="number" name="billableRate" value={formData.billableRate} onChange={handleChange} className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 75.00" />
                    </div>
                </div>
                {/* r6-b2a-costrate-ui-5f8d2c19 — B2a labor-COST rate (per hour; blank defaults to wage server-side via getLaborCostRate) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Cost Rate <span className="font-normal text-gray-400">(what they cost you /hr)</span></label>
                    <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input type="number" name="laborCostRate" value={compEditable ? formData.laborCostRate : ''} onChange={handleChange} disabled={!compEditable} className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400" placeholder={compState === 'loading' ? 'Loading…' : compState === 'denied' ? 'Hidden' : 'e.g. 45.00'} />
                    </div>
                </div>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">Bonus/Commission Plan Details</label>
                <textarea name="bonusPlan" value={formData.bonusPlan} onChange={handleChange} rows="2" className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
            </div>
            
            {/* ── WORK SCHEDULE & SHIFT TRACKING ── */}
            <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-gray-800">Work Schedule & Shift Tracking</h3>
                    {!canEditSchedule && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">View only</span>
                    )}
                </div>
                <p className="text-xs text-gray-400 mb-4">
                    Leave on <span className="font-medium text-gray-500">Use org default</span> unless this employee works a different schedule.
                    These settings control how payroll estimates missing shifts and calculates expected hours.
                    {!canEditSchedule && <span className="text-amber-600 font-medium"> Only Admins and Owners can edit schedule settings.</span>}
                </p>

                {/* Schedule preset selector */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Schedule</label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {[
                            { value: '',       label: 'Org default', sub: 'recommended' },
                            { value: '5x8',    label: '5 × 8h',      sub: '40h week'   },
                            { value: '4x10',   label: '4 × 10h',     sub: '40h week'   },
                            { value: '3x12',   label: '3 × 12h',     sub: '36h week'   },
                            { value: 'custom', label: 'Custom',       sub: 'set below'  },
                        ].map(opt => {
                            const active = formData.schedulePreset === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleScheduleChange('schedulePreset', opt.value)}
                                    disabled={!canEditSchedule}
                                    className={`flex flex-col items-center justify-center rounded-lg border-2 py-2 px-1 text-xs font-semibold transition-colors
                                        ${active
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'
                                        } ${!canEditSchedule ? 'opacity-60 cursor-not-allowed hover:border-gray-200' : ''}`}
                                >
                                    {opt.label}
                                    <span className={`font-normal mt-0.5 ${active ? 'text-blue-400' : 'text-gray-400'}`}>{opt.sub}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Custom hours inputs */}
                    {formData.schedulePreset === 'custom' && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Hours per day</label>
                                <input
                                    type="number" min="1" max="24" step="0.5"
                                    value={formData.workHoursPerDay}
                                    onChange={e => handleScheduleChange('workHoursPerDay', e.target.value)}
                                    disabled={!canEditSchedule}
                                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                />
                            </div>
                        </div>
                    )}

                    {/* Day-of-week selector — shown whenever a preset is chosen */}
                    {formData.schedulePreset !== '' && (
                        <div className="mt-4">
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                                Which days does this employee work?
                            </label>
                            {(() => {
                                const activeDays = formData.workDays || PRESET_WORK_DAYS[formData.schedulePreset] || [];
                                const cap = formData.schedulePreset === 'custom'
                                    ? 7
                                    : SCHEDULE_PRESETS[formData.schedulePreset]?.[0] ?? 7;
                                const atCap = activeDays.length >= cap;
                                return (
                                    <>
                                        <div className="flex gap-1.5 flex-wrap">
                                            {ALL_DAYS.map(day => {
                                                const isChecked = activeDays.includes(day.key);
                                                const isDisabled = !canEditSchedule || (atCap && !isChecked);
                                                return (
                                                    <button
                                                        key={day.key}
                                                        type="button"
                                                        onClick={() => handleDayToggle(day.key)}
                                                        disabled={isDisabled}
                                                        title={
                                                            !canEditSchedule
                                                                ? 'Only Admins and Owners can edit schedule settings'
                                                                : (atCap && !isChecked)
                                                                    ? `Deselect a day first — maximum ${cap} days for this schedule`
                                                                    : ''
                                                        }
                                                        className={`w-10 h-10 rounded-lg text-xs font-semibold border-2 transition-colors
                                                            ${isChecked
                                                                ? 'border-blue-600 bg-blue-600 text-white'
                                                                : isDisabled
                                                                    ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                                                                    : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'
                                                            }`}
                                                    >
                                                        {day.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1.5">
                                            {activeDays.length} of {cap === 7 ? 7 : cap} day{cap !== 1 ? 's' : ''} selected
                                            {formData.schedulePreset !== 'custom' && ` (max ${cap} for this schedule)`}
                                            {formData.workHoursPerDay ? ` · ${formData.workHoursPerDay}h/day` : ''}
                                        </p>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {/* Shift tracking override */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shift Tracking Mode</label>
                    <div className="space-y-1.5">
                        {[
                            { value: '',         label: 'Use org default',              desc: 'Follows the setting in Payroll Settings.' },
                            { value: 'strict',   label: 'Strict — must punch in/out',   desc: 'Missing shifts block timesheet approval.' },
                            { value: 'estimate', label: 'Estimate — propose from jobs', desc: 'Missing shifts estimated from job time. One-click confirm.' },
                            { value: 'off',      label: 'Off — use scheduled hours',    desc: "No shift punches required. Hours come from this employee's schedule." },
                        ].map(opt => {
                            const active = formData.shiftTrackingMode === opt.value;
                            return (
                                <label
                                    key={opt.value}
                                    className={`flex items-start gap-3 p-2.5 rounded-lg border-2 transition-colors
                                        ${active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-200'}
                                        ${!canEditSchedule ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <input
                                        type="radio"
                                        name="empShiftTrackingMode"
                                        value={opt.value}
                                        checked={active}
                                        onChange={() => handleScheduleChange('shiftTrackingMode', opt.value)}
                                        disabled={!canEditSchedule}
                                        className="mt-0.5 accent-blue-600"
                                    />
                                    <div>
                                        <p className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-gray-700'}`}>{opt.label}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── DIRECT DEPOSIT ── */}
            <div className="pt-4 border-t">
                <h3 className="font-semibold mb-2 flex items-center gap-2"><FileText size={18} className="text-gray-500" /> Direct Deposit Form</h3>
                
                {formData.directDepositFormUrl ? (
                    <div className="flex justify-between items-center bg-green-50 p-3 rounded-xl border border-green-200">
                         <div className="flex items-center gap-2 overflow-hidden">
                            <FileText size={20} className="text-green-600 flex-shrink-0" />
                            <a href={formData.directDepositFormUrl} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline truncate">
                                Direct Deposit Form (Uploaded)
                            </a>
                        </div>
                        <button onClick={() => handleDeleteDocument('Direct Deposit Form', 'directDepositFormUrl')} className="text-red-500 hover:text-red-700 ml-2">
                            <Trash2 size={16} />
                        </button>
                    </div>
                ) : (
                    isNew ? (
                         <div className="bg-yellow-50 p-3 rounded-xl border border-yellow-200 text-sm text-yellow-700 flex items-center mb-3">
                            <AlertTriangle size={16} className="mr-2" />
                            Save user to upload form.
                        </div>
                    ) : (
                        isUploading && activeUploadField === 'directDepositFormUrl' ? (
                            <div className="w-full bg-gray-100 rounded-xl p-4 border border-gray-200">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                    <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                                </div>
                                <p className="text-xs text-center text-gray-500">Uploading...</p>
                            </div>
                        ) : (
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-gray-50 transition-colors relative group cursor-pointer">
                                <input 
                                    type="file" 
                                    onChange={(e) => handleFileUpload(e, 'directDepositFormUrl')}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                />
                                <UploadCloud className="mx-auto h-6 w-6 text-gray-400 group-hover:text-blue-500 transition-colors mb-2" />
                                <p className="text-sm text-gray-600 font-medium">Click to upload form</p>
                            </div>
                        )
                    )
                )}
            </div>
        </div>
    );

    const renderTimeOff = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Weeks Vacation Allotted</label>
                    <input type="number" name="vacationWeeksAllotted" value={formData.vacationWeeksAllotted} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 2" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Stats</label>
                    <div className="text-sm mt-2 space-y-1">
                        <p>Total Days: <span className="font-bold">{daysAllotted}</span></p>
                        <p>Remaining: <span className={`font-bold ${vacationDaysRemaining < 0 ? 'text-red-600' : 'text-green-600'}`}>{vacationDaysRemaining}</span></p>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-lg font-bold mb-2">Log Time Off</h3>
                {isNew ? (
                    <div className="bg-yellow-100 p-3 rounded-xl text-sm text-yellow-800 flex items-center">
                        <AlertTriangle size={16} className="mr-2 flex-shrink-0" />
                        Please save the new employee before logging time off history.
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-2 items-end bg-blue-50 p-3 rounded-xl border border-blue-100">
                        <div className="col-span-3">
                            <label className="text-xs font-semibold">Start</label>
                            <input type="date" value={newTimeOff.start} onChange={e => setNewTimeOff({...newTimeOff, start: e.target.value})} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="col-span-3">
                            <label className="text-xs font-semibold">End</label>
                            <input type="date" value={newTimeOff.end} onChange={e => setNewTimeOff({...newTimeOff, end: e.target.value})} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs font-semibold">Type</label>
                            <select value={newTimeOff.type} onChange={e => setNewTimeOff({...newTimeOff, type: e.target.value})} className="w-full appearance-none border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-7">
                                <option>Vacation</option><option>Sick</option><option>Personal</option>
                            </select>
                        </div>
                        <div className="col-span-3">
                            <label className="text-xs font-semibold">Upload (Optional)</label>
                            <input type="file" onChange={e => setNewTimeOff({...newTimeOff, file: e.target.files[0]})} className="w-full text-xs border p-1 rounded bg-white" />
                        </div>
                        <div className="col-span-1">
                            <button onClick={handleAddTimeOff} disabled={isUploading || isNew} className="w-full py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400">
                                Add
                            </button>
                        </div>
                         <div className="col-span-12">
                             <input type="text" value={newTimeOff.notes} onChange={e => setNewTimeOff({...newTimeOff, notes: e.target.value})} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notes (Optional)" />
                        </div>
                    </div>
                )}

                <div className="mt-4 border rounded-xl overflow-hidden">
                    <h4 className="px-3 py-2 bg-gray-200 font-semibold">Time Off History</h4>
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-2 text-left">Dates</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-center">Days</th>
                                <th className="px-3 py-2 text-left">Notes</th>
                                <th className="px-3 py-2 text-left">Doc</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {(formData.timeOffHistory || []).map((entry) => (
                                <tr key={entry.id}>
                                    <td className="px-3 py-2 whitespace-nowrap">{entry.startDate} to {entry.endDate}</td>
                                    <td className="px-3 py-2 whitespace-nowrap"><span className={`px-2 py-0.5 rounded-full text-xs ${entry.type === 'Vacation' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{entry.type}</span></td>
                                    <td className="px-3 py-2 text-center">{entry.days}</td>
                                    <td className="px-3 py-2 max-w-[150px] truncate">{entry.notes || '-'}</td>
                                    <td className="px-3 py-2">
                                        {entry.docUrl && <a href={entry.docUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center"><Calendar size={14} className="mr-1"/> Doc</a>}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => handleDeleteTimeOff(entry.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14}/></button>
                                    </td>
                                </tr>
                            ))}
                            {(formData.timeOffHistory || []).length === 0 && <tr><td colSpan="6" className="text-center py-4 text-gray-500">No time off recorded.</td></tr>}
                        </tbody>
                    </table>
                </div>
                
                <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
                    <div className="bg-green-50 p-2 rounded border border-green-100">Vacation Used: <strong>{vacationDaysTaken}</strong></div>
                    <div className="bg-yellow-50 p-2 rounded border border-yellow-100">Sick Used: <strong>{sickDaysTaken}</strong></div>
                    <div className="bg-gray-50 p-2 rounded border border-gray-100">Personal Used: <strong>{personalDaysTaken}</strong></div>
                </div>
            </div>
        </div>
    );
    
    const renderEducation = () => (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold">Educational History</h3>
            <div>
                <label className="block text-sm font-medium text-gray-700">Undergraduate Degree</label>
                <input type="text" name="undergradDegree" value={formData.undergradDegree} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., B.Sc. Computer Science" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Masters Degree</label>
                <input type="text" name="mastersDegree" value={formData.mastersDegree} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., MBA or M.Eng" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Doctorate/Professional Degree</label>
                <input type="text" name="doctorateDegree" value={formData.doctorateDegree} onChange={handleChange} className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., Ph.D. or J.D." />
            </div>
        </div>
    );

    const renderDocuments = () => (
        <div className="space-y-6">
            <DocumentUploadSection 
                title="Certifications & Training" 
                fieldName="certifications" 
                docs={formData.certifications} 
                icon={FileText} 
            />
            <hr />
            <DocumentUploadSection 
                title="Disciplinary / HR Forms" 
                fieldName="disciplinaryForms" 
                docs={formData.disciplinaryForms} 
                icon={AlertTriangle} 
            />
        </div>
    );

    const renderEmergency = () => (
        <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Emergency Contact</h3>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <input
                        type="text"
                        value={formData.emergencyName}
                        onChange={e => setFormData(p => ({ ...p, emergencyName: e.target.value }))}
                        placeholder="e.g. Jane Smith"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Relationship</label>
                    <input
                        type="text"
                        value={formData.emergencyRelationship}
                        onChange={e => setFormData(p => ({ ...p, emergencyRelationship: e.target.value }))}
                        placeholder="e.g. Spouse, Parent, Sibling"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Primary Phone</label>
                    <input
                        type="tel"
                        name="emergencyPhone1"
                        value={formData.emergencyPhone1}
                        onChange={handleChange}
                        placeholder="e.g. (555) 123-4567"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Secondary Phone</label>
                    <input
                        type="tel"
                        name="emergencyPhone2"
                        value={formData.emergencyPhone2}
                        onChange={handleChange}
                        placeholder="e.g. (555) 987-6543"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                        type="email"
                        value={formData.emergencyEmail}
                        onChange={e => setFormData(p => ({ ...p, emergencyEmail: e.target.value }))}
                        placeholder="e.g. jane.smith@email.com"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'main': return renderMainInfo();
            case 'wages': 
                if (!can(currentUserProfile, PERMISSIONS.VIEW_SENSITIVE_INFO)) return null;
                return renderWages();
            case 'timeOff': return renderTimeOff();
            case 'education': return renderEducation();
            case 'documents': return renderDocuments();
            case 'emergency': return renderEmergency();
            default: return renderMainInfo();
        }
    };

    const mainModal = (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center sm:p-4 z-[90] backdrop-blur-sm">
            <div className="bg-white w-full sm:rounded-2xl sm:max-w-4xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">
                
                <div className="flex justify-between items-center px-6 pt-5 pb-4 border-b flex-shrink-0">
                    {/* tmdupe-4c7a1e93 — formData.name, not editingEmployee.name: the prop is null
                        for a user created in this session, and this line threw a TypeError the
                        moment `isNew` started telling the truth. */}
                    <h2 className="text-xl font-bold text-gray-900">{isNew ? 'Add New Employee' : formData.name}</h2>
                    <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 p-1"><X size={22} /></button>
                </div>

                <div className="flex gap-0.5 px-4 pt-3 pb-0 border-b overflow-x-auto flex-shrink-0">
                    <TabButton name="main" label="Main Info" />
                    {can(currentUserProfile, PERMISSIONS.VIEW_SENSITIVE_INFO) && (
                        <TabButton name="wages" label="Wages & Pay" />
                    )}
                    <TabButton name="timeOff" label="Time Off" />
                    <TabButton name="education" label="Education" />
                    <TabButton name="documents" label="Documents" />
                    <TabButton name="emergency" label="Emergency Contact" />
                </div>

                {(error || successMessage) && (
                    <div className={`mx-6 mt-3 px-3 py-2 rounded-xl text-sm flex items-center gap-2 ${
                        error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                        {error || successMessage}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {renderTabContent()}
                </div>

                <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50 sm:rounded-b-2xl flex-shrink-0 gap-2">
                    <div className="flex items-center gap-2">
                        {!isNew && (
                            <button onClick={() => setShowTerminate(true)}
                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                                <ShieldAlert size={14} /> End Employment
                            </button>
                        )}
                        {!isNew && !(editingEmployee?.role || '').includes('Owner') && editingEmployee?.email !== currentUserProfile?.email && (
                            <button onClick={handleDowngrade} disabled={isSaving}
                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 disabled:opacity-50 transition-colors">
                                <UserMinus size={14} /> Downgrade to Team Member
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={closeModal} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                        <button onClick={handleSave} disabled={isSaving || isUploading}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors">
                            {isSaving ? 'Saving...' : (isNew ? 'Create Employee' : 'Save Changes')}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );

    return (
        <>
            {mainModal}
            {showTerminate && (
                <TerminationModal
                    // tmdupe-4c7a1e93 — targetUid, not the prop: for a user created in this session
                    // both of these were undefined, which would have written to users/undefined.
                    teamMember={{ id: targetUid, name: formData.name, activatedUid: targetUid }}
                    currentUserProfile={currentUserProfile}
                    onClose={(didTerminate) => {
                        setShowTerminate(false);
                        if (didTerminate) closeModal();
                    }}
                />
            )}
        </>
    );
};

export default EmployeeProfileModal;